/**
 * Общий диалог — затемнение `.dim` + карточка `.modal` из base.css (макет Approve.html).
 *
 * Почему это отдельный компонент, а не три копии разметки в трёх диалогах решения: у
 * диалога есть три вещи, которые в копиях расходятся молча и обнаруживаются только на
 * приёмке — Escape, возврат фокуса на элемент, с которого диалог открыли, и связь
 * `aria-labelledby` с заголовком. Диалоги решения — это места, где человек даёт согласие
 * по FR-4.1; «почти работающая» клавиатура здесь недопустима.
 *
 * Портал в `document.body` нужен по существу, а не для красоты: панель решения лежит в
 * колонке с `overflow` и собственным контекстом наложения, и диалог внутри неё обрезался бы
 * по границе колонки.
 */

import { type JSX, type ReactNode, useCallback, useEffect, useId, useRef } from 'react';
import { createPortal } from 'react-dom';
import { IconX } from './icons.tsx';
import styles from './Modal.module.css';

export interface ModalProps {
  readonly title: string;
  /** Мета справа от заголовка: идентификатор черновика, номер версии. */
  readonly subtitle?: string;
  readonly width?: number;
  readonly onClose: () => void;
  readonly children: ReactNode;
}

export function Modal({
  title,
  subtitle,
  width = 600,
  onClose,
  children,
}: ModalProps): JSX.Element {
  const titleId = useId();
  const cardRef = useRef<HTMLDivElement | null>(null);
  const returnFocusTo = useRef<Element | null>(null);

  const close = useCallback(() => {
    onClose();
  }, [onClose]);

  useEffect(() => {
    returnFocusTo.current = document.activeElement;
    cardRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        close();
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      const back = returnFocusTo.current;
      if (back instanceof HTMLElement) back.focus();
    };
  }, [close]);

  return createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 40 }}>
      {/* Затемнение закрывает диалог по клику. Кнопкой, а не div с onClick: иначе это
          интерактивный элемент, которого нет ни для клавиатуры, ни для скринридера. */}
      <button
        type="button"
        className="dim"
        aria-label="Закрыть"
        onClick={close}
        style={{ border: 'none', padding: 0, cursor: 'default' }}
      />
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          /* Поле слоя: 24px от края окна на десктопе и 8px на телефоне. На 390px постоянные
             24px отбирали у карточки восьмую часть ширины — а именно там она и нужна. Сама
             карточка ужимается своим правилом в base.css (≤640: padding 16, radius 12). */
          padding: 'clamp(8px, 3vw, 24px)',
          pointerEvents: 'none',
        }}
      >
        <div
          ref={cardRef}
          className={`modal ${styles['card'] ?? ''}`}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
          tabIndex={-1}
          style={{
            width,
            maxWidth: '100%',
            maxHeight: '100%',
            overflowY: 'auto',
            pointerEvents: 'auto',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div className="h2" id={titleId} style={{ fontSize: 20 }}>
              {title}
            </div>
            {subtitle === undefined ? null : <span className="meta">{subtitle}</span>}
            <div style={{ flex: 1 }} />
            <button
              type="button"
              onClick={close}
              aria-label="Закрыть"
              style={{
                background: 'transparent',
                border: 'none',
                color: 'var(--muted)',
                display: 'flex',
                cursor: 'pointer',
                padding: 4,
              }}
            >
              <IconX />
            </button>
          </div>
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
}
